import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { askMoo } from '../../../queries/ask-moo';

const POLL_INTERVAL_MS = 60000;

/** Conversation ids arrive in both dashed-UUID and undashed-hex forms —
 *  the active-runs endpoint returns the undashed canonical form. */
export function normalizeConversationId(id: string): string {
  return id.replace(/-/g, '').toLowerCase();
}

/**
 * In-flight background runs across all conversations, as a map of
 * normalized conversation id → running run count. Look up with
 * `counts[normalizeConversationId(conv.id)]`.
 *
 * Backed by a shared react-query subscription — one 10s poll per session
 * no matter how many components mount this (e.g. the chat panel and its
 * drawer open together), instead of one polling loop per mount.
 */
function is404(error: unknown): boolean {
  const status =
    (error as { response?: { status?: number }; status?: number })?.response
      ?.status ?? (error as { status?: number })?.status;
  return status === 404;
}

export function useActiveAsyncRuns(enabled = true): Record<string, number> {
  const query = useQuery({
    ...askMoo.activeAsyncRuns(),
    enabled,
    // Poll on a fixed cadence, but stop entirely once we learn the feature is
    // off: either the backend reports `enabled: false` (current contract), or
    // the endpoint 404s (older deployments that predate that field). Either
    // way there is nothing to poll for. Transient failures (5xx, network)
    // keep the interval alive. The last known counts are retained.
    refetchInterval: (_data, query) =>
      query.state.data?.enabled === false || is404(query.state.error)
        ? false
        : POLL_INTERVAL_MS,
    retry: false,
  });

  return useMemo(() => {
    const counts: Record<string, number> = {};
    for (const entry of query.data?.active ?? []) {
      counts[entry.conversation_id] = entry.running_count;
    }
    return counts;
  }, [query.data]);
}
